import Vue from "vue";
import { Component, Inject } from "vue-property-decorator";
import ChatWindow from "@/modules/Home/components/ChatWindow/ChatWindow.vue";
import { ChatAssistantService } from "./services/ChatAssistant.service";
import { FORM_CONSTANTS } from "@/shared/constants/FormConstants";
import { UserService } from "@/modules/User/services/UserService";
import { UserPagination } from "@/modules/User/models/UserPagination";
import { SEARCH_PATIENT_CONSTANTS } from "@/shared/components/SearchPatient/constants/SearchPatientConstants";
import { USER_STATES } from "@/modules/User/constants/UserConstants";
import { Response } from "@/shared/models/Response";
import { IMG_SOURCE } from "@/shared/components/ChatBot/constants/ChatBotConstants";

interface Conversation {
  id: string;
  patientId: string;
  patientDocument: string;
  title: string;
  preview: string;
  time: string;
  initials: string;
}

interface PatientResult {
  id: string;
  name: string;
  document: string;
  initials: string;
}

@Component({
  name: "ChatAssistant",
  components: { ChatWindow }
})
export default class ChatAssistant extends Vue {
  @Inject() chatAssistantService!: ChatAssistantService;
  @Inject() userService!: UserService;

  search = "";
  patientDocument = "";
  selectedConversation = "general";
  showSidebar = true;
  isSearchingPatient = false;
  patientSearchLoading = false;
  patientNotFound = false;
  patientResult: PatientResult | null = null;
  PATIENT_NOT_FOUND_MESSAGE = `Paciente no encontrado en ${FORM_CONSTANTS.PAGE_TITLE}`;

  conversations: Conversation[] = [];
  CHAT_BOT_IMAGE = IMG_SOURCE;

  created(): void {
    void this.loadActiveSessions();
  }

  async loadActiveSessions(patientId?: string): Promise<void> {
    try {
      const response = await this.chatAssistantService.getActiveSessions();
      const data = (response.data ?? response) as any;
      const sessions = Array.isArray(data)
        ? data
        : data?.sessions || data?.activeSessions || data?.data || [];
      this.conversations = sessions
        .slice()
        .sort(
          (left: any, right: any) =>
            this.getTimestamp(right.dateChanged || right.dateCreated) -
            this.getTimestamp(left.dateChanged || left.dateCreated)
        )
        .map((session: any) => {
          const title = session.patientName || "Conversación";
          return {
            id: session.id || session.sessionId,
            patientId: session.patientId,
            patientDocument: session.patientDocument,
            title,
            preview: "",
            time: this.formatSessionTime(session.dateChanged),
            initials: title
              .split(" ")
              .filter(Boolean)
              .slice(0, 2)
              .map((part: string) => part.charAt(0).toUpperCase())
              .join("")
          };
        })
        .filter((conversation: Conversation) => Boolean(conversation.id));
      const selected = patientId
        ? this.conversations.find(
            conversation => conversation.patientId === patientId
          )
        : undefined;
      this.selectedConversation =
        selected?.id || this.conversations[0]?.id || "";
      this.setActiveConversation(
        this.conversations.find(item => item.id === this.selectedConversation)
          ?.patientId || ""
      );
    } catch (error) {
      console.error("Error loading active chat sessions", error);
    }
  }

  async handleSessionCreated(patientId?: string): Promise<void> {
    await this.loadActiveSessions(patientId);
  }

  formatSessionTime(timestamp: any): string {
    if (!timestamp) return "";

    const seconds = timestamp._seconds ?? timestamp.seconds;
    const nanoseconds = timestamp._nanoseconds ?? timestamp.nanoseconds ?? 0;
    if (typeof seconds !== "number") return "";

    const date = new Date(seconds * 1000 + nanoseconds / 1000000);
    const elapsedMinutes = Math.floor((Date.now() - date.getTime()) / 60000);

    if (elapsedMinutes < 1) return "Ahora";
    if (elapsedMinutes < 60) return `${elapsedMinutes} min`;

    const elapsedHours = Math.floor(elapsedMinutes / 60);
    if (elapsedHours < 24) return `${elapsedHours} h`;

    return date.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  }

  startNewConversation(): void {
    this.isSearchingPatient = true;
    this.patientDocument = "";
    this.patientNotFound = false;
    this.patientResult = null;
  }

  cancelNewConversation(): void {
    this.isSearchingPatient = false;
    this.patientDocument = "";
    this.patientNotFound = false;
    this.patientResult = null;
  }

  async searchPatients(): Promise<void> {
    const document = this.patientDocument.trim();
    if (!document || this.patientSearchLoading) return;

    this.patientSearchLoading = true;
    this.patientNotFound = false;
    try {
      let responseData: Response<UserPagination>;
      try {
        responseData = await this.userService.getUserByFilters({
          ...SEARCH_PATIENT_CONSTANTS.FILTER_QUERY_DEFAULT,
          userDocument: document,
          userState: USER_STATES.ACTIVE
        });
      } catch {
        this.patientResult = null;
        this.patientNotFound = true;
        return;
      }

      const patients = responseData.data?.userArray ?? [];
      if (!patients.length) {
        this.patientResult = null;
        this.patientNotFound = true;
        return;
      }

      const patient = await this.chatAssistantService.searchPatients(document);
      this.patientResult = {
        id: patient.id,
        name: patient.name,
        document: patient.document,
        initials: patient.name
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((part: string) => part.charAt(0).toUpperCase())
          .join("")
      };
    } catch (error) {
      console.error("Error searching patients", error);
      this.patientResult = null;
    } finally {
      this.patientSearchLoading = false;
    }
  }

  selectPatient(patient: PatientResult): void {
    this.conversations = [
      {
        id: `new-${patient.id}`,
        patientId: patient.id,
        title: patient.name,
        preview: "Nueva conversación",
        time: "Ahora",
        initials: patient.initials,
        patientDocument: patient.document
      },
      ...this.conversations
    ];
    this.selectedConversation = `new-${patient.id}`;
    this.setActiveConversation(patient.id);
    this.isSearchingPatient = false;
    this.search = "";
    this.patientDocument = "";
    this.showSidebar = false;
  }

  get filteredConversations(): Conversation[] {
    const query = this.search.trim().toLowerCase();
    if (!query) return this.conversations;
    return this.conversations.filter(conversation =>
      `${conversation.title} ${conversation.preview}`
        .toLowerCase()
        .includes(query)
    );
  }

  get activeConversation(): Conversation {
    return (
      this.conversations.find(({ id }) => id === this.selectedConversation) ||
      this.conversations[0] || {
        id: "",
        patientId: "",
        title: "Sin conversaciones activas",
        preview: "",
        time: "",
        initials: ""
      }
    );
  }

  get hasActiveConversation(): boolean {
    return Boolean(this.activeConversation.id);
  }

  selectConversation(id: string): void {
    this.selectedConversation = id;
    const conversation = this.conversations.find(item => item.id === id);
    this.setActiveConversation(conversation?.patientId || "");
    this.showSidebar = false;
  }

  getTimestamp(timestamp: any): number {
    if (!timestamp) return 0;
    if (typeof timestamp === "number") return timestamp;
    if (timestamp instanceof Date) return timestamp.getTime();

    const seconds = timestamp._seconds ?? timestamp.seconds;
    const nanoseconds = timestamp._nanoseconds ?? timestamp.nanoseconds ?? 0;
    if (typeof seconds === "number") {
      return seconds * 1000 + nanoseconds / 1000000;
    }

    const parsed = new Date(timestamp).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  setActiveConversation(conversationId: string): void {
    if (!conversationId) return;
    this.$store.dispatch("chatassistant/markAsRead", conversationId);
  }

  conversationStatus(conversation: Conversation): "working" | "ready" | "" {
    const state = this.$store.getters["chatassistant/conversation"](
      conversation.patientId
    );
    if (state.loading && conversation.id !== this.selectedConversation) {
      return "working";
    }
    if (state.hasUnreadResult) return "ready";
    return "";
  }
}
